import { memo, useMemo } from 'react';
import { ResourceProfile } from '@raydiant/api-client-js';
import Text from '@raydiant/raydial/components/Text';
import { LayoutOptions } from '../utils';
import UserIcon from './assets/user-icon.svg';
import DataTable from '../../../components/DataTable/DataTable';
import DataTableHeader from '../../../components/DataTable/DataTableHeader';
import DataTableCell from '../../../components/DataTable/DataTableCell';
import ApplicationIcon from './components/ApplicationIcon';
import UserCard from './components/UserCard/UserCard';

interface DomainProfilesLayoutProps {
  layout: LayoutOptions;
  domainProfiles: ResourceProfile[];
  onClick: (id: string) => void;
}

const MemoizedUserCard = memo(UserCard);

const DomainProfilesLayout = ({
  layout,
  domainProfiles,
  onClick,
}: DomainProfilesLayoutProps) => {
  const gridView = useMemo(
    () =>
      domainProfiles.map((user) => (
        <MemoizedUserCard
          key={user.id}
          user={user}
          onClick={() => onClick(user.id)}
        />
      )),
    [domainProfiles, onClick],
  );

  const listView = useMemo(
    () => (
      <div className="w-full">
        <DataTable<ResourceProfile>
          currentPage={0}
          data={domainProfiles || []}
          showPagination={false}
          headers={
            <tr>
              <DataTableHeader<ResourceProfile>
                className="!text-left !bg-gray-100 !text-gray-500 !fill-gray-500"
                wrapperClassName="!justify-start"
                onSort={() => {}}
                showSort={false}
              >
                <Text className="text-base font-semibold pl-8">name</Text>
              </DataTableHeader>
              <DataTableHeader<ResourceProfile>
                className="!bg-gray-100 !text-gray-500 !fill-gray-500"
                wrapperClassName="!justify-start"
                onSort={() => {}}
                showSort={false}
                sortDirection={undefined}
              >
                <Text className="text-base font-semibold">email</Text>
              </DataTableHeader>
              <DataTableHeader<ResourceProfile>
                className="!bg-gray-100 !text-gray-500 !fill-gray-500"
                wrapperClassName="!justify-start"
                onSort={() => {}}
                showSort={false}
                sortDirection={undefined}
              >
                <Text className="text-base font-semibold">domainrole</Text>
              </DataTableHeader>
            </tr>
          }
          onPageChange={() => {}}
          pageCount={1}
          renderRow={(profile) => (
            <tr
              key={profile.id}
              onClick={() => onClick(profile.id)}
              className="cursor-pointer"
            >
              <DataTableCell
                hasSort
                className="!rounded-2xl p-2.5"
                wrapperClassName="text-left flex"
              >
                <ApplicationIcon className="mr-4" url={UserIcon} />
                <Text className="text-lg w-[191px] truncate">
                  {profile.name}
                </Text>
              </DataTableCell>
              <DataTableCell
                className="p-2.5"
                wrapperClassName="min-w-[240px] text-left"
              >
                <Text className="w-[217px] text-lg truncate">
                  {profile.email}
                </Text>
              </DataTableCell>
              <DataTableCell
                className="p-2.5"
                wrapperClassName="min-w-[240px] text-left"
              >
                <Text className="w-[217px] text-lg truncate">
                  {profile.domainRole}
                </Text>
              </DataTableCell>
            </tr>
          )}
        />
      </div>
    ),
    [domainProfiles, onClick],
  );

  return (
    <>
      {layout === 'grid' && (
        <div className="flex flex-wrap gap-6 justify-center md:justify-start">
          {gridView}
        </div>
      )}
      {layout === 'list' && listView}
    </>
  );
};

export default DomainProfilesLayout;
